import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import { Input, Modal } from '@unified-checkout/design-system';

import type {
  IAddDeletePhonePayload,
  IPasswordConfirmationModal,
  TModalAction,
} from '@app/dashboard/types/innerLayout/profile_details/confirmation-modal';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  MODAL_ACTIONS,
  MODAL_CONFIRMATION_CONFIG,
  PHONE_NUMBER_INITIAL_STATE,
  PROFILE_INFO_FIELDS,
} from '@app/dashboard/constants/settings/profile-details';
import { notify } from '@app/dashboard/utils/notifier';
import { onFieldChangeHandler } from '@app/dashboard/utils/formValidations';
import { updateMerchantDetails } from '@app/dashboard/services/inner_layout/profile_details';
import { getMerchantProfileData } from '@app/dashboard/utils/localStorageUtils';

import styles from './PasswordConfirmation.module.scss';

const PasswordConfirmation = ({
  fields,
  setFields,
  isModalOpen,
  setIsModalOpen,
  handleAfterConfirmation,
  modalAction,
  targetPhoneNumber,
  phoneNumbers,
}: IPasswordConfirmationModal) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const queryClient = useQueryClient();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const modalConfig = MODAL_CONFIRMATION_CONFIG[modalAction as TModalAction];
  const accountID = getMerchantProfileData().id;

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetValue = e.target.value;
    onFieldChangeHandler(
      fields,
      setFields,
      PROFILE_INFO_FIELDS.PASSWORD,
      targetValue,
      () => {}
    );
  };

  const { isPending: isSaveChangesLoading, mutate: saveChangesMutation } =
    useMutation({
      mutationFn: ({
        merchantId,
        data,
      }: {
        merchantId: string;
        data: IAddDeletePhonePayload;
      }) => updateMerchantDetails(merchantId, data),
      onSuccess: async () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_PROFILE_DETAILS],
        });
        notify.success(t(modalConfig.SUCCESS_MESSAGE_LOCALIZE_KEY));
        handleAfterConfirmation?.();
        setFields(PHONE_NUMBER_INITIAL_STATE);
        setIsModalOpen(false);
      },
      onError: (axiosErr: AxiosError<{ message?: string }>) => {
        let message = t('errors.somethingWentWrong');

        if (axiosErr.response?.data?.message) {
          message = axiosErr.response.data.message;
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }

        notify.error(message);
      },
    });

  const handleClick = () => {
    let phones;
    if (modalAction === MODAL_ACTIONS.ADD) {
      phones = [...phoneNumbers, targetPhoneNumber];
    } else {
      phones = phoneNumbers.filter((phone) => phone !== targetPhoneNumber);
    }

    saveChangesMutation({
      merchantId: accountID,
      data: { phones, password: fields.password.value as string },
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isModalOpen) {
      setFields((prev) => ({
        ...prev,
        [PROFILE_INFO_FIELDS.PASSWORD]:
          PHONE_NUMBER_INITIAL_STATE[PROFILE_INFO_FIELDS.PASSWORD],
      }));
    }
  }, [isModalOpen]);

  return (
    <Modal
      open={isModalOpen}
      isOverlayAboveModal
      isMobile={isMobile}
      title={t(modalConfig?.TITLE_LOCALIZE_KEY, {
        phoneNumber: targetPhoneNumber,
      })}
      btnsProps={[
        {
          size: 'md',
          color: 'primary',
          variant: 'contained',
          text: t('innerLayout.profileDetailsPage.actions.confirm'),
          onClickHandler: handleClick,
          disabled:
            !fields[PROFILE_INFO_FIELDS.PASSWORD].isValid ||
            isSaveChangesLoading,
          loading: isSaveChangesLoading,
        },
        {
          size: 'md',
          color: 'secondary',
          variant: 'outlined',
          text: t('innerLayout.profileDetailsPage.actions.cancel'),
          onClickHandler: handleCancel,
          disabled: isSaveChangesLoading,
        },
      ]}
    >
      <div className={styles['password-confirmation__input']}>
        <Input
          name={PROFILE_INFO_FIELDS.PASSWORD}
          type="password"
          label={t('innerLayout.profileDetailsPage.form.password')}
          placeholder={t('innerLayout.profileDetailsPage.modal.placeholder')}
          onChange={handleFieldChange}
          value={fields[PROFILE_INFO_FIELDS.PASSWORD].value}
          error={fields[PROFILE_INFO_FIELDS.PASSWORD].errorMsg}
          status={
            fields[PROFILE_INFO_FIELDS.PASSWORD].errorMsg ? 'error' : 'default'
          }
        />
      </div>
    </Modal>
  );
};

export default PasswordConfirmation;
